"use client";
import cx from "classnames";
import TeamPlayerAvatar from "@/components/teamPlayerAvatar";
import {
  HEAD_TO_HEAD_PLAYER_LINK_CLICKED,
  HEAD_TO_HEAD_STATUS_MAP,
} from "@/constants/amplitude";
import { NFL } from "@/constants/sports";
import { GetGameInjuriesQuery } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import styles from "./Injuries.module.scss";

const IR = "INJURED_RESERVE";
const NA = "NOT_ACTIVE";
const QUESTIONABLE = "QUESTIONABLE";
const PROBABLE = "PROBABLE";
const OUT = "OUT";

interface PlayerProps {
  player: NonNullable<
    NonNullable<NonNullable<GetGameInjuriesQuery["event"]>["playerInjuries"]>[0]
  >;
}

interface PlayerDetailsProps {
  playerName: string;
  playerPosition?: string | null;
  jerseyUrl?: string | null;
  playerPageUrl?: string | null;
  ampModule?: string;
}

export const PlayerDetails = ({
  playerName,
  playerPosition,
  jerseyUrl,
  playerPageUrl,
  ampModule,
}: PlayerDetailsProps) => {
  const { eventName, gameState } = useHeadToHeadContext();
  const { logAmplitudeEvent } = useAmplitude();
  const handleClick = () => {
    logAmplitudeEvent(HEAD_TO_HEAD_PLAYER_LINK_CLICKED, {
      Sport: NFL,
      "Event Name": eventName,
      Module: ampModule ?? "unknown",
      "Link URL": playerPageUrl ?? "unknown",
      "Player Name": playerName,
      "Page State": HEAD_TO_HEAD_STATUS_MAP[gameState],
    });
  };
  return (
    <div className={styles.playerInfo}>
      <TeamPlayerAvatar
        containerClassName={styles.avatarContainer}
        primaryImgName={playerName}
        primaryImgUrl={jerseyUrl}
        primaryImgSize={30}
        primaryImgClassName={styles.primaryImg}
      />
      <span className={styles.playersPosition}>{playerPosition ?? "-"}</span>
      {playerPageUrl ? (
        <a href={playerPageUrl} onClick={handleClick}>
          <span className={styles.playerName}>{playerName}</span>
        </a>
      ) : (
        <span className={styles.playerName}>{playerName}</span>
      )}
    </div>
  );
};

export const InjuryBadge = ({ player }: PlayerProps) => {
  if (player.injury?.status === IR) {
    return (
      <div className={cx(styles.injuryBadge, styles.injuryIR)}>
        <span className={styles.injuryTag}>IR</span>
        <span className={styles.injuryDesc}>{player.injury?.description}</span>
      </div>
    );
  }

  if (player.injury?.status === NA) {
    return (
      <div className={cx(styles.injuryBadge, styles.injuryNA)}>
        <span className={styles.injuryTag}>PUP</span>
        <span className={styles.injuryDesc}>{player.injury?.description}</span>
      </div>
    );
  }

  if (player.injury?.status === QUESTIONABLE) {
    return (
      <div className={cx(styles.injuryBadge, styles.injuryQ)}>
        <span className={styles.injuryTag}>Q</span>
        <span className={styles.injuryDesc}>{player.injury?.description}</span>
      </div>
    );
  }

  if (player.injury?.status === PROBABLE) {
    return (
      <div className={cx(styles.injuryBadge, styles.injuryP)}>
        <span className={styles.injuryTag}>P</span>
        <span className={styles.injuryDesc}>{player.injury?.description}</span>
      </div>
    );
  }

  if (player.injury?.status === OUT) {
    return (
      <div className={cx(styles.injuryBadge, styles.injuryO)}>
        <span className={styles.injuryTag}>O</span>
        <span className={styles.injuryDesc}>{player.injury?.description}</span>
      </div>
    );
  }
  return (
    <div className={cx(styles.injuryBadge, styles.injuryO)}>
      <span className={styles.injuryTag}>O</span>
      <span className={styles.injuryDesc}>{player.injury?.description}</span>
    </div>
  );
};
